import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Ticket, X, Inbox, Reply, UserPlus, ExternalLink } from 'lucide-react';
import api from '../../utils/api';
import CompanyLogo from '../CompanyLogo';
import useDraggable from '../../hooks/useDraggable';
import FeatureSpotlight, { useFeatureSpotlight } from '../FeatureSpotlight';
import { track } from '../../utils/trackAction';
import { bubblePanelStyle } from '../../utils/bubblePanel';
import { onTicketsChanged } from '../../utils/ticketEvents';

/**
 * TicketsBubble — la burbuja de tickets, hermana de la de WhatsApp.
 *
 * Por qué existe. Se midió que en 30 días WhatsApp tuvo 379 tickets atendidos con una
 * mediana de respuesta de 0 horas, mientras que por mail quedaron 24 tickets vivos sin
 * una sola respuesta humana, el más viejo de 27 días. Entrevistados por separado, Ariel
 * y Santiago dijeron lo mismo: lo primero que miran al llegar es la burbuja de WhatsApp,
 * y los tickets "hay que revisarlos a mano". Ariel además pidió esta burbuja por escrito
 * el 7-may-2026 y el pedido estuvo 117 días sin respuesta.
 *
 * El diagnóstico es que WhatsApp EMPUJA (suena, aparece un número, no te lo podés perder)
 * y los tickets hay que ir a buscarlos. Cuando algo depende de que la persona se acuerde
 * de mirar, falla — no por distracción, sino porque esa disciplina no le sale a nadie
 * todos los días.
 *
 * Qué muestra, que es exactamente lo que pidieron:
 *   · SIN RESPONDER  — nadie contestó nunca (el pedido de Santiago: "una cola de los que
 *     no fueron atendidos")
 *   · ESPERANDO      — ya contestamos pero el cliente volvió a escribir (el pedido de
 *     Ariel: "no sé qué es nuevo y qué ya está respondido")
 *
 * Los datos salen de /tickets/attention-count?list=1, la MISMA consulta que alimenta el
 * badge del menú: si el número y la lista salieran de queries distintas se contradirían,
 * y un aviso en el que no confiás es peor que no tener aviso.
 *
 * Deliberadamente NO se responde desde acá (v1): el objetivo es que dejes de tener que ir
 * a buscar. Contestar sin salir de la burbuja es el paso siguiente, no éste.
 *
 * CUÁNDO APARECE. Sólo si hay algo que atender. Es la diferencia con la de WhatsApp y
 * la del chat, que están siempre porque desde ellas también se INICIA una conversación:
 * ésta no inicia nada, es puramente un aviso de "hay clientes esperando". Con la cola en
 * cero no tiene nada que mostrar y ser un tercer círculo permanente en el mismo rincón
 * es ensuciar la pantalla sin dar información. Que aparezca sólo cuando hay algo la hace
 * MÁS visible, no menos: si está, es porque hay trabajo. Los tickets siguen estando en el
 * menú y en el listado completo, que es donde se va a buscar a propósito.
 *
 * POSICIÓN. Arrastrable con useDraggable, igual que la de WhatsApp, el chat, el timer de
 * proyectos y las demás. No es un adorno: el hook mantiene un registro de ocupación
 * compartido entre todas las burbujas, así que dos que caen en la misma zona se APILAN
 * en vez de taparse. Cuando esta burbuja se posicionaba con un `left` fijo en CSS no
 * figuraba en ese registro — las otras no sabían que existía y cualquiera podía quedar
 * encima. El hook además resuelve el alto de la barra inferior y el área segura del
 * teléfono, que la versión con CSS a mano ignoraba.
 */

const POLL_MS = 60000;
const SEEN_KEY = 'tickets_bubble_seen_v1';

export default function TicketsBubble() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [tab, setTab] = useState('all');
  const seenRef = useRef(new Set());
  // Instrumentación: cuándo se abrió el panel y si la visita terminó en algo.
  // Viven acá arriba porque `go` (que marca el uso) está en este componente y el
  // botón que abre y cierra está en el hijo — los dos tienen que ver el mismo ref.
  const openedAt = useRef(null);
  const usedRef = useRef(false);

  useEffect(() => {
    try { seenRef.current = new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]')); } catch { /* primera vez */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/tickets/attention-count?list=1');
      setTickets(data.tickets || []);
    } catch { /* silencioso: es un aviso, no puede romper la pantalla */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  // Además del poll: recargar en el acto cuando alguien toca un ticket desde otra
  // pantalla. Sin esto, mandar un ticket a la papelera lo dejaba hasta 60 segundos
  // acá adentro — clickeable, llevando a un ticket que ya no está (el #12704 de Andy).
  useEffect(() => onTicketsChanged(load), [load]);

  const sinResponder = tickets.filter(x => x.never_answered);
  const esperando = tickets.filter(x => !x.never_answered);
  // "Sin asignar" es una pregunta distinta de las otras dos: no es "¿le contestamos?"
  // sino "¿alguien se hizo cargo?". Un ticket puede estar contestado y seguir sin
  // dueño, y ése es el que se cae del radar — nadie lo siente propio.
  const sinDuenio = tickets.filter(x => x.unassigned);
  const visibles = tab === 'new' ? sinResponder
    : tab === 'waiting' ? esperando
    : tab === 'unassigned' ? sinDuenio
    : tickets;

  // "Nuevo" = no lo vimos nunca en la burbuja. Marca los que aparecieron desde la última
  // vez que abriste, que es la pregunta real ("¿qué entró mientras no miraba?").
  const nuevos = tickets.filter(x => !seenRef.current.has(x.id)).length;

  const markSeen = () => {
    const ids = tickets.map(x => x.id);
    seenRef.current = new Set(ids);
    try { localStorage.setItem(SEEN_KEY, JSON.stringify(ids.slice(-300))); } catch { /* modo privado */ }
  };

  // La medida de si la burbuja sirve no es cuántas veces se abre: es cuántas veces
  // alguien SALE de ella hacia un ticket. Abrirla y cerrarla sin tocar nada es la
  // señal opuesta, y por eso también se registra (bubble_close con result abandon).
  const go = (id) => {
    usedRef.current = true;
    track('tickets', 'bubble_open_ticket', tab, { result: 'ok' });
    setOpen(false);
    navigate(`/tickets/${id}`);
  };
  const goList = () => {
    usedRef.current = true;
    track('tickets', 'bubble_open_list', tab, { result: 'ok' });
    setOpen(false);
    navigate('/tickets');
  };

  const since = (iso) => {
    if (!iso) return '';
    const h = (Date.now() - new Date(iso).getTime()) / 36e5;
    if (h < 1) return t('ticketsBubble.minutesAgo', { n: Math.max(1, Math.round(h * 60)) });
    if (h < 48) return t('ticketsBubble.hoursAgo', { n: Math.round(h) });
    return t('ticketsBubble.daysAgo', { n: Math.round(h / 24) });
  };

  // Si el panel está abierto no se esconde aunque la cola llegue a cero: sacarle el
  // panel de las manos a alguien que acaba de resolver el último ticket sería peor.
  if (tickets.length === 0 && !open) return null;

  return (
    <TicketsBubbleUI
      t={t} open={open} setOpen={setOpen} tickets={tickets} nuevos={nuevos}
      tab={tab} setTab={setTab} visibles={visibles}
      sinResponder={sinResponder} esperando={esperando} sinDuenio={sinDuenio}
      markSeen={markSeen} go={go} goList={goList} since={since}
      openedAt={openedAt} usedRef={usedRef}
    />
  );
}

/**
 * La parte visual, en su propio componente por una razón concreta: useDraggable reserva
 * el lugar de la burbuja en la pila compartida durante el MONTAJE, sin mirar si el
 * componente llegó a dibujar algo. Si el hook viviera arriba, con la cola en cero la
 * burbuja escondida seguiría ocupando su casillero y las otras dejarían un hueco vacío
 * entre ellas. Montando esto sólo cuando hay algo que mostrar, el registro queda limpio.
 */
function TicketsBubbleUI({ t, open, setOpen, tickets, nuevos, tab, setTab, visibles, sinResponder, esperando, sinDuenio, markSeen, go, goList, since, openedAt, usedRef }) {
  const { pos: dragPos, isDragging, wasDragged, handlers: dragHandlers, elementRef: fabRef } =
    useDraggable('tk-bubble-pos', { defaultZone: 'bottom-right', width: 52, height: 52 });
  // Se anuncia sola la primera vez. No es opcional acá: por el bug del gate esta burbuja
  // existió durante días sólo para los admins, así que para todo agente es nueva hoy — y
  // que se pueda arrastrar no lo descubre nadie si no se lo decimos.
  const spot = useFeatureSpotlight('tickets_bubble_v1');

  const fab = (
    <button
      // Dos refs sobre el mismo nodo: useDraggable lo mide (callback ref) y el
      // spotlight lo usa de ancla (ref object).
      ref={(el) => { fabRef(el); spot.anchorRef.current = el; }}
      className={`tk-bubble-fab${nuevos > 0 ? ' has-unread' : ''}`}
      style={{
        position: 'fixed', left: dragPos.x, top: dragPos.y,
        right: 'auto', bottom: 'auto',
        cursor: isDragging ? 'grabbing' : 'grab',
        transition: isDragging ? 'none' : 'left 0.18s ease, top 0.18s ease',
        touchAction: 'none',
      }}
      // wasDragged evita que soltar el arrastre cuente como click y abra el panel.
      onClick={() => {
        if (wasDragged.current) return;
        if (open) {
          // Cuánto tiempo estuvo abierta y si se fue con las manos vacías.
          track('tickets', 'bubble_close', null, {
            durationMs: openedAt.current ? Date.now() - openedAt.current : null,
            result: usedRef.current ? 'ok' : 'abandon',
          });
          openedAt.current = null;
          usedRef.current = false;
        } else {
          openedAt.current = Date.now();
          track('tickets', 'bubble_open', String(tickets.length), { result: 'ok' });
          markSeen();
        }
        setOpen(o => !o);
      }}
      title={t('ticketsBubble.title')}
      aria-label={t('ticketsBubble.title')}
      {...dragHandlers}
    >
      <Ticket size={22} color="#fff" />
      {tickets.length > 0 && (
        <span className="tk-bubble-badge">{tickets.length > 99 ? '99+' : tickets.length}</span>
      )}
    </button>
  );

  const row = (x) => (
    <div key={x.id} className="tk-bubble-item" onClick={() => go(x.id)}>
      <div className="tk-bubble-item-top">
        {x.company_name && <CompanyLogo name={x.company_name} logoUrl={x.company_logo_url} labelColor={x.company_color} size={16} />}
        <span className="tk-bubble-num">#{x.ticket_number}</span>
        <span className={`tk-bubble-tag ${x.never_answered ? 'is-new' : 'is-waiting'}`}>
          {x.never_answered ? t('ticketsBubble.neverAnswered') : t('ticketsBubble.waitingReply')}
        </span>
        <span className="tk-bubble-age">{since(x.last_activity_at)}</span>
      </div>
      <div className="tk-bubble-subject">{x.subject || '—'}</div>
      <div className="tk-bubble-meta">
        {x.company_name || t('ticketsBubble.noCompany')}
        {x.contact_name ? ` · ${x.contact_name}` : ''}
        {x.unassigned ? ` · ${t('ticketsBubble.unassigned')}` : ''}
      </div>
    </div>
  );

  const panel = open && (
    <div className="tk-bubble-panel" style={bubblePanelStyle(dragPos, { width: 360, height: 520 })}>
      <div className="tk-bubble-header">
        <Ticket size={16} />
        <span style={{ fontWeight: 700 }}>{t('ticketsBubble.title')}</span>
        <button className="tk-bubble-close" onClick={() => setOpen(false)} aria-label={t('common.close')}>
          <X size={16} />
        </button>
      </div>

      <div className="tk-bubble-tabs">
        <button className={tab === 'all' ? 'active' : ''} onClick={() => { track('tickets', 'bubble_tab', 'all'); setTab('all'); }}>
          {t('ticketsBubble.tabAll')} ({tickets.length})
        </button>
        <button className={tab === 'new' ? 'active' : ''} onClick={() => { track('tickets', 'bubble_tab', 'new'); setTab('new'); }}>
          <Inbox size={12} /> {t('ticketsBubble.tabNever')} ({sinResponder.length})
        </button>
        <button className={tab === 'waiting' ? 'active' : ''} onClick={() => { track('tickets', 'bubble_tab', 'waiting'); setTab('waiting'); }}>
          <Reply size={12} /> {t('ticketsBubble.tabWaiting')} ({esperando.length})
        </button>
        {/* Se muestra sólo si hay alguno. Un agente común nunca ve tickets sin dueño
            (el endpoint se los da únicamente a managers y admins), así que para él la
            pestaña estaría siempre en cero: una opción que no hace nada es peor que
            no tenerla. Mismo criterio que el botón de limpiar filtros. */}
        {sinDuenio.length > 0 && (
          <button className={tab === 'unassigned' ? 'active' : ''} onClick={() => { track('tickets', 'bubble_tab', 'unassigned'); setTab('unassigned'); }}>
            <UserPlus size={12} /> {t('ticketsBubble.tabUnassigned')} ({sinDuenio.length})
          </button>
        )}
      </div>

      <div className="tk-bubble-list">
        {visibles.length === 0
          ? <div className="tk-bubble-empty">{t('ticketsBubble.allClear')}</div>
          : visibles.map(row)}
      </div>

      <button className="tk-bubble-footer" onClick={goList}>
        <ExternalLink size={13} /> {t('ticketsBubble.openList')}
      </button>
    </div>
  );

  return ReactDOM.createPortal(
    <>
      {fab}
      {panel}
      <FeatureSpotlight
        anchorRef={spot.anchorRef}
        // El spotlight espera a que la burbuja exista de verdad: anunciarla mientras
        // está escondida sería señalar un lugar vacío.
        show={spot.shouldShow && !open}
        onDismiss={spot.dismiss}
        title={t('ticketsBubble.spotlightTitle')}
        body={t('ticketsBubble.spotlightBody')}
        placement="left"
      />
    </>,
    document.body,
  );
}
